import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "" });

  const loadTenants = async () => {
    try {
      const { data } = await api.get("/tenants");
      setTenants(data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadTenants(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tenants", form);
      toast.success("Franchise created");
      setShowAdd(false);
      setForm({ name: "", slug: "" });
      loadTenants();
    } catch { toast.error("Failed to create franchise"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this franchise?")) return;
    try { await api.delete(`/tenants/${id}`); toast.success("Franchise deactivated"); loadTenants(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="tenants-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A]">Franchise Management</h1>
          <p className="text-sm text-[#6B7280]">Manage your franchise network</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="gap-2 bg-[#0F172A]" data-testid="add-tenant-btn">
          <Plus size={16} /> Add Franchise
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bm-table-header">
                <TableHead className="text-xs">Name</TableHead>
                <TableHead className="text-xs">Slug</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Created</TableHead>
                <TableHead className="text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1, 2].map((i) => <TableRow key={i}><TableCell colSpan={5}><div className="h-8 skeleton-shimmer rounded" /></TableCell></TableRow>)
              ) : tenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Storefront size={40} className="mx-auto text-[#D1D5DB] mb-3" />
                    <p className="text-sm text-[#6B7280]">No franchises yet</p>
                  </TableCell>
                </TableRow>
              ) : tenants.map((t) => (
                <TableRow key={t.id} className="hover:bg-[#F9FAFB]">
                  <TableCell className="font-medium text-[#0F172A]">{t.name}</TableCell>
                  <TableCell className="text-sm text-[#6B7280] font-mono">{t.slug}</TableCell>
                  <TableCell><Badge className={t.is_active !== false ? "bg-green-100 text-green-800 text-[10px]" : "bg-red-100 text-red-800 text-[10px]"}>{t.is_active !== false ? "Active" : "Inactive"}</Badge></TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-[#6B7280]">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "-"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} className="text-[#EF4444] hover:bg-red-50 text-xs" data-testid={`delete-tenant-${t.id}`}>Deactivate</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-[#0F172A]">Create Franchise</DialogTitle></DialogHeader>
          <form onSubmit={handleAdd} className="space-y-3">
            <div><Label className="text-xs">Franchise Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-10 border-[#E5E7EB]" data-testid="tenant-name-input" /></div>
            <div><Label className="text-xs">Slug (URL identifier)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated if empty" className="h-10 border-[#E5E7EB]" /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#0F172A]" data-testid="submit-tenant-btn">Create</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
